import { AppBar, Container, Box, Button, Divider, Drawer, List, ListItemButton, ListItemText, Toolbar, Typography, IconButton } from '@mui/material';
import Grid2 from '@mui/material/Unstable_Grid2';
import MenuIcon from '@mui/icons-material/Menu';
import React, { useContext, useEffect, useState } from 'react'
import { Top } from './ManagementPages/Top';
import { PreRegistration } from './ManagementPages/PreRegistration';
import { WorkRegistration } from './ManagementPages/WorkRegistration';
import { WorkAndCertificate } from './ManagementPages/WorkAndCertificate';
import { ChangeTargetYear } from './ManagementPages/ChangeTargetYear';
import { ChangePeriod } from './ManagementPages/ChangePeriod';
import { CountryMaster } from './ManagementPages/CountryMaster';
import { RegionMaster } from './ManagementPages/RegionMaster';
import { UserMaster } from './ManagementPages/UserMaster';
import { AuthorityMaster } from './ManagementPages/AuthorityMaster';
import { Loading } from './ManagementPages/Loading';
import { useInit } from './DataFetch/useInit';
import { SessionContext } from './ManagementRoot';
import { ChangeHeldTimes } from './ManagementPages/ChangeHeldTimes';

interface PropsType {
    /**
     * ログアウト処理
     */
    Logout: () => void
}

export function Management({Logout}:PropsType) {

    /**
     * 画面表示に必要なデータをロード
     * ロードのみでこの画面ではデータを使用しない
     * ロード完了後、画面を表示する
     */
    const { isLoading } = useInit();

    /**
     * メニューの幅
     */
    const drawerWidth = 240;

    /**
     * 画面縮小時のメニュー開閉フラグ
     */
    const [menuOpen, setMenuOpen] = useState(false);

    /**
     * メニュー開閉フラグ切替処理
     */
    const handleDrawerToggle = () => {
        setMenuOpen(!menuOpen);
    };

    /**
     * ページ名称を定義
     */
    const PageNames = {
        Top: "Top",
        PreRegistration: "PreRegistration",
        WorkRegistration: "WorkRegistration",
        WorkAndCertificate: "WorkAndCertificate",
        ChangeTargetYear: "ChangeTargetYear",
        ChangeHeldTimes: "ChangeHeldTimes",
        ChangePeriod: "ChangePeriod",
        CountryMaster: "CountryMaster",
        RegionMaster: "RegionMaster",
        UserMaster: "UserMaster",
        AuthorityMaster: "AuthorityMaster",
    }

    /**
     * 各画面を呼び出すプロパティ
     * PageNamesと組み合わせて使用する
     */
    const Pages: Record<string, JSX.Element> = {
        "Top": <Top/>,
        "PreRegistration": <PreRegistration/>,
        "WorkRegistration": <WorkRegistration/>,
        "WorkAndCertificate": <WorkAndCertificate/>,
        "ChangeTargetYear": <ChangeTargetYear/>,
        "ChangeHeldTimes" : <ChangeHeldTimes/>,
        "ChangePeriod": <ChangePeriod/>,
        "CountryMaster": <CountryMaster/>,
        "RegionMaster": <RegionMaster/>,
        "UserMaster": <UserMaster/>,
        "AuthorityMaster": <AuthorityMaster/>,
    }

    /**
     * 表示するページ名を格納
     */
    const [pageName, setPageName] = useState(PageNames.Top);

    /**
     * セッション継続確認処理をContextから取得
     */
    const ConfirmSession = useContext(SessionContext);

    /**
     * ロード時にセッション確認
     */
    useEffect(() => {
        ConfirmSession()
    },[ConfirmSession])

    /**
     * ヘッダーコンポーネント
     */
    function Header() {
        return(
            <>
                <AppBar position="fixed" color='primary'
                    sx={{
                        width: { sm: `calc(100% - ${drawerWidth}px)` },
                        ml: { sm: `${drawerWidth}px` },
                    }}
                >
                    <Container maxWidth="xl">
                        <Toolbar disableGutters>
                            <IconButton
                                color="inherit"
                                onClick={handleDrawerToggle}
                                sx={{ mr: 2, display: { sm: 'none' } }}
                            >
                                <MenuIcon/>
                            </IconButton>
                            <Box>
                                <Typography
                                    variant="h6"
                                    noWrap
                                >
                                    KAGU管理
                                </Typography>
                            </Box>
                            <Box sx={{flexGrow: 1}}></Box>
                            <Box>
                                <Button variant="contained" 
                                    sx={{
                                        backgroundColor:"gray",
                                        ":hover":{
                                            backgroundColor:"grey.600",
                                        }
                                        
                                    }}
                                    onClick={Logout}
                                >
                                    ログアウト
                                </Button>
                            </Box>                          
                        </Toolbar>
                    </Container>
                </AppBar>
            </>
        );
    }

    /**
     * フッターコンポーネント
     */
    function Footer() {
        return(
            <>
                <Box height="10px" bgcolor="grey.200"></Box>
                <Box
                    component="footer"
                    borderTop={1}
                    ml={{ sm: `${drawerWidth}px` }}
                    borderColor="grey.300"
                    sx={{
                        mt: "auto",
                        backgroundColor:"white",
                        borderTopColor:"primary",
                        width: { sm: `calc(100% - ${drawerWidth}px)` },
                    }}
                >
                    <Container maxWidth="xl" disableGutters>
                        <Grid2 container>
                            <Grid2>
                                <Typography variant="body1" textAlign="left" ml={1}>
                                    Copyright(C)<br/>
                                    Photo town HIGASHIKAWA All Rights Reserved.
                                </Typography>
                            </Grid2>
                            <Box sx={{flexGrow: 1}}></Box>
                            <Grid2>
                                <Typography variant="body1" textAlign="right" mr={1}>
                                    Version 2.0
                                </Typography>
                            </Grid2>
                        </Grid2>
                    </Container>          
                </Box>
            </>
        );
    }

    /**
     * 左サイドメニュー表示要素
     */
    const DrawerItem = (
        <>
            <Toolbar/>
            <Divider/>
            <List>
                <ListItemButton onClick={() => setPageName(PageNames.Top)}>
                    <ListItemText primary="トップ"/>
                </ListItemButton>
                <ListItemButton onClick={() => setPageName(PageNames.PreRegistration)}>
                    <ListItemText primary="事前登録"/>
                </ListItemButton>
                <ListItemButton onClick={() => setPageName(PageNames.WorkRegistration)}>
                    <ListItemText primary="本登録"/>
                </ListItemButton>
                <ListItemButton onClick={() => setPageName(PageNames.WorkAndCertificate)}>
                    <ListItemText primary="作品と在籍証明"/>
                </ListItemButton>
                <ListItemButton onClick={() => setPageName(PageNames.ChangeTargetYear)}>
                    <ListItemText primary="年度変更"/>
                </ListItemButton>
                <ListItemButton onClick={() => setPageName(PageNames.ChangeHeldTimes)}>
                    <ListItemText primary="開催回変更"/>
                </ListItemButton>
                <ListItemButton onClick={() => setPageName(PageNames.ChangePeriod)}>
                    <ListItemText primary="受付期間変更"/>
                </ListItemButton>
            </List>
            <Divider/>
            <List>
                <ListItemButton onClick={() => setPageName(PageNames.CountryMaster)}>
                    <ListItemText primary="国マスタ"/>
                </ListItemButton>
                <ListItemButton onClick={() => setPageName(PageNames.RegionMaster)}>
                    <ListItemText primary="地域マスタ"/>
                </ListItemButton>
                <ListItemButton onClick={() => setPageName(PageNames.UserMaster)}>
                    <ListItemText primary="ユーザーマスタ"/>
                </ListItemButton>
                <ListItemButton onClick={() => setPageName(PageNames.AuthorityMaster)}>
                    <ListItemText primary="権限マスタ"/>
                </ListItemButton>
            </List>
        </>
    );

    /**
     * 画面全体のコンポーネント
     */
    function Dashboard() {
        return(
            <>
                <Header/>

                {/* サイドメニュー */}
                <Box component="nav">
                    {/* 画面縮小時 */}
                    <Drawer
                        variant="temporary"
                        open={menuOpen}
                        onClose={handleDrawerToggle}
                        ModalProps={{
                            keepMounted: true,
                        }}
                        sx={{
                            display: { xs: 'block', sm: 'none' },
                            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
                        }}
                    >
                        {DrawerItem}
                    </Drawer>

                    {/* 通常画面 */}
                    <Drawer
                        variant="permanent"
                        sx={{
                            display: { xs: 'none', sm: 'block' },
                            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
                        }}
                        open
                        >
                        {DrawerItem}
                    </Drawer>
                </Box>

                {/* 各画面 */}
                <Box
                    component="main"
                    sx={{ flexGrow: 1, width: { sm: `calc(100% - ${drawerWidth}px)` }, ml:{sm:`${drawerWidth}px`}, bgcolor:"grey.200"}}
                >
                    <Toolbar />
                    <Container maxWidth="xl" sx={{mt:4}}>
                        <Grid2 container spacing={3}>
                            {Pages[pageName]}
                        </Grid2>
                    </Container>
                </Box>
                <Footer/>
            </>
        );
    }

    return(
        // initデータがロード完了したら画面を表示
        isLoading ? <Loading/> : <Dashboard/>
    );
}